import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, map, takeUntil, switchMap, tap } from 'rxjs/operators';

import { FormViews, ThumbnailKinds, Template } from '@site-mate/dashpivot-shared-library';

import { FormService } from 'app/form/form.service';
import { WINDOW } from 'app/shared/factory/window.factory';
import { FormWeb } from 'app/shared/model/form.model';
import { AppUtilService } from 'app/shared/service/app-util.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { RouteParamsService } from 'app/shared/service/route-params.service';

import { Timeline } from './timeline';
import { FormModalAction } from '../../template-form-edit/form-modal-action.model';

@Component({
  selector: 'cc-form-list',
  templateUrl: 'form-list.component.html',
  styleUrls: ['form-list.component.scss'],
})
export class FormListComponent implements OnInit, OnDestroy {
  @Input() template: Template;
  @Input() canAddForm: boolean;

  @Output()
  onEditForm = new EventEmitter();

  @ViewChild('infiniteScrollParentElem') public infiniteScrollParentElem: ElementRef;
  @ViewChild('infiniteScrollChildElem') public infiniteScrollChildElem: ElementRef;

  sortFormsBy: string;
  timeline: Timeline;
  limit = 30;
  offset = 0;
  end;
  loading = true;
  loadingMore: boolean;
  private unSubscribe = new Subject<void>();

  constructor(
    private readonly appUtilService: AppUtilService,
    private readonly formService: FormService,
    private readonly errorHandler: ErrorHandler,
    private readonly routeParamsService: RouteParamsService,
    private readonly router: Router,
    @Inject(WINDOW) private readonly window: Window,
  ) {}

  ngOnInit(): void {
    this.initForms();
    this.addFormToList();
    this.updateListOnFormDelete();
    this.updateListOnFormSave();
    this.cloneFormIntoList();
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  private initForms() {
    this.routeParamsService
      .get('template')
      .pipe(
        takeUntil(this.unSubscribe),
        filter((template) => !!template),
      )
      .subscribe((template) => {
        this.template = template;
        const templatesSortFormsBy = this.template.sortFormsBy?.[0];
        const sortByItem =
          templatesSortFormsBy && this.template.items.find((item) => item.id === templatesSortFormsBy);
        this.sortFormsBy = sortByItem && sortByItem.description;

        this.loadFormList();
      });
  }

  private addFormToList() {
    this.routeEventsObservable(FormModalAction.Add)
      .pipe(
        switchMap((formId: string) =>
          this.formService
            .listTemplateForms(this.template.id, { queryForm: formId, limit: this.timeline.formsSize + 1 })
            .pipe(takeUntil(this.unSubscribe)),
        ),
      )
      .subscribe((forms) => {
        this.timeline.addFormsToTimeline(forms, true);
        this.offset = this.timeline.formsSize;
      }, this.errorHandler.handle.bind(this.errorHandler));
  }

  private updateListOnFormSave() {
    this.routeEventsObservable(FormModalAction.Save)
      .pipe(
        tap((formId: string) => this.timeline.setFormInUpdatingState(formId)),
        switchMap((formId: string) => {
          return this.formService
            .listTemplateForms(this.template.id, { queryForm: formId, limit: this.timeline.formsSize })
            .pipe(
              takeUntil(this.unSubscribe),
              map((forms: FormWeb[]) => ({ forms, formId })),
            );
        }),
      )
      .subscribe(({ forms, formId }) => {
        if (forms.length) {
          this.timeline.updateFormInGroup(forms);
        } else {
          this.removeFormFromTimeline(formId);
        }
      }, this.errorHandler.handle.bind(this.errorHandler));
  }

  private updateListOnFormDelete() {
    this.routeEventsObservable(FormModalAction.Delete).subscribe(this.removeFormFromTimeline.bind(this));
  }

  private cloneFormIntoList() {
    this.router.events
      .pipe(
        takeUntil(this.unSubscribe),
        filter((event) => event instanceof NavigationEnd),
        filter(() => this.router.getCurrentNavigation().extras?.state?.reason === FormModalAction.Clone),
      )
      .subscribe(this.loadFormList.bind(this));
  }

  private removeFormFromTimeline(formId) {
    this.timeline.removeFormFromGroup(formId);
    this.offset = this.timeline.formsSize;
  }

  private routeEventsObservable(action: FormModalAction) {
    return this.router.events.pipe(
      takeUntil(this.unSubscribe),
      filter((event) => event instanceof NavigationEnd),
      filter(() => this.router.getCurrentNavigation().extras?.state?.reason === action),
      map(() => this.router.getCurrentNavigation().extras.state?.formId),
    );
  }

  private getFormsRequestParams() {
    const isThumbAFieldId = (thumb) =>
      ![ThumbnailKinds.CreatedAt, ThumbnailKinds.CreatedBy, ThumbnailKinds.Counter].includes(thumb);

    return {
      limit: this.limit.toString(),
      skip: null,
      items: this.template.displayFormThumbnailBy.filter(Boolean).filter(isThumbAFieldId),
      view: FormViews.Timeline,
    };
  }

  private loadFormList(): void {
    this.loading = true;
    this.offset = 0;
    this.end = false;

    this.timeline = new Timeline(this.template.sortFormsBy[0]);

    this.onScroll();
  }

  onScroll(): void {
    if (this.end || this.loadingMore) {
      return;
    }

    const params = this.getFormsRequestParams();
    params.skip = this.offset.toString();
    this.loadingMore = true;

    this.formService
      .listTemplateForms(this.template.id, params)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(
        (forms) => {
          this.offset += this.limit;
          this.end = forms.length < this.limit;
          this.timeline.addFormsToTimeline(forms);
          this.loading = false;
          this.loadingMore = false;

          // timeout is needed to check after the list has been rendered
          this.window.setTimeout(() => {
            // if there is no scrollbar, but the end is not still reached
            if (
              this.infiniteScrollParentElem.nativeElement.clientHeight >
              this.infiniteScrollChildElem.nativeElement.scrollHeight
            ) {
              this.onScroll();
            }
          }, 0);
        },
        (error) => {
          this.errorHandler.handle(error);
          this.loading = false;
          this.loadingMore = false;
        },
      );
  }

  isScrollWindow(): boolean {
    return this.appUtilService.isSmallWindow;
  }
}
